import { useCallback } from 'react';
import {
  useNavigate as useRouterNavigate,
  useSearchParams,
  type NavigateOptions,
  type To,
} from 'react-router-dom';

import { useAuthContext } from 'src/auth/hooks';
import { appendPlatformId } from 'src/utils/append-platform-id';

/**
 * Custom useNavigate hook that ensures platform_id is always preserved in search params.
 * Works exactly like the native useNavigate from react-router-dom.
 */
export const useNavigate = () => {
  const routerNavigate = useRouterNavigate();
  const [searchParams] = useSearchParams();
  const { platformId } = useAuthContext();

  const navigate = useCallback(
    (to: To | number, options?: NavigateOptions) => {
      // Handle history navigation (e.g., routerNavigate(-1))
      if (typeof to === 'number') {
        routerNavigate(to);
        return;
      }

      const currentPlatformId = platformId || searchParams.get('platform_id');
      routerNavigate(appendPlatformId(to, currentPlatformId), options);
    },
    [routerNavigate, platformId, searchParams]
  );

  return navigate;
};
